/**
 * The gift room, redesigned (2026-10-29, Chris approved round 3:
 * Claude outputs/gift-room-2026-10-29/DESIGN.md, prototype.html).
 *
 * One thing per step, seven steps; the gift stays in view (the left page on a
 * computer, a slim strip of cards on a phone); seven quiet dots say where you
 * are; one gold action per step; the ✕ top right on every step.
 *
 *   1 arrival     the gift as a letter, "Draw my card"
 *   2 draw        one line for a newcomer, the question held, the deck
 *   3 your card   the card and its reading; "Tell <sender> what it meant" (gold),
 *                 "Ask my own question" (quiet)
 *   4 thank       the reply card; sent: the receipt, the thanks, the circle ask,
 *                 then "See how our stars meet"
 *   5 birthday    the date only; the sign in plain words; into the star
 *   6 the star    You & <sender>: the five arms, the plain words
 *   7 your own    For me · For someone over one gold button
 *
 * Chris's overrides (2026-10-29): no "On us" pill and no new free-reading rule
 * (the gold button carries the normal reading price, when prices show); no
 * sender birthday guess; no "Keep this… portrait from your stars" line.
 *
 * Pure helpers only: GiftRoomSteps and GiftStar draw them, ReaderPage drives them.
 */
import { GLYPHS, signIndex } from '@/utils/birth-ask'

export const STEP_COUNT = 7

/** The step the room opens on, from the server's gift_room state. */
export function stepForRoom (room) {
  const r = room || {}
  if (r.deeper !== 'drawn') return 1
  if (r.stars) return 6
  if (r.thanked) return 4
  return 3
}

/**
 * A step this device remembers (back from their own reading, a reload in the
 * birthday) wins when it is further on than the server's, except the draw
 * (the deck opens from the letter's gold button) and anything past the letter
 * before their card is drawn.
 */
export function resumeStep (serverStep, remembered) {
  const s = Number(serverStep) || 1
  const m = Number(remembered) || 0
  if (s < 3 || m === 2 || m <= s || m > STEP_COUNT) return s
  return m
}

const KEY = 'ma_gr_step_'
export function rememberStep (inv, step) {
  if (!inv) return
  try { sessionStorage.setItem(KEY + inv, String(step)) } catch (e) { /* a nicety */ }
}
export function rememberedStep (inv) {
  if (!inv) return 0
  try { return Number(sessionStorage.getItem(KEY + inv)) || 0 } catch (e) { return 0 }
}

// The Quest's "Tell <sender> what it meant" (funnel gap 6, 2026-10-30): the
// room opens at the reply card once, whatever step it remembers.
const OPEN_AT = 'ma_gr_open_'
export function openRoomAt (inv, step) {
  if (!inv) return
  try { sessionStorage.setItem(OPEN_AT + inv, String(step)) } catch (e) { /* a nicety */ }
}
export function takeOpenAt (inv) {
  if (!inv) return 0
  try {
    const n = Number(sessionStorage.getItem(OPEN_AT + inv)) || 0
    sessionStorage.removeItem(OPEN_AT + inv)
    return n >= 1 && n <= STEP_COUNT ? n : 0
  } catch (e) { return 0 }
}

export function capFirst (text = '') {
  const t = String(text || '')
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t
}

// ── The words of each step ────────────────────────────────────────────────

export const DRAW_LINE = "Tarot is a deck of 78 picture cards. Hold the question below, then pick the card that pulls at you. There's no wrong one."
export const HELD_EYEBROW = 'The question you hold'

export function cardAsk (sender = '') {
  const who = capFirst(String(sender || '').trim()) || 'They'
  return `${who} drew these with you in mind. A few words back would mean a lot.`
}

export function starsLead (sender = '') {
  const who = String(sender || '').trim()
  return who
    ? `One more thing, if you'd like: I can show you how your stars and ${who}'s meet. It starts with your birthday.`
    : "One more thing, if you'd like: I can show you how your stars meet. It starts with your birthday."
}

export const BIRTHDAY_ASK = 'When were you born? The day is enough.'

/** The Sun sign in plain words: what it is like, never what it predicts. */
export const SIGN_WORDS = {
  Aries: 'bold, quick to start, happiest when there is something to begin.',
  Taurus: 'steady, patient, at home in the good, simple things.',
  Gemini: 'curious, quick-witted, always ready for a good conversation.',
  Cancer: 'caring, protective, at your best looking after your people.',
  Leo: 'warm, generous, glad to be seen and quick to light up a room.',
  Virgo: 'thoughtful, careful, quietly making things better.',
  Libra: 'fair-minded, easygoing, always looking for the balance.',
  Scorpio: 'deep, loyal, slow to trust and true once you do.',
  Sagittarius: 'curious, open-handed, always looking past the horizon.',
  Capricorn: 'patient, determined, building something that lasts.',
  Aquarius: 'independent, original, thinking about how things could be.',
  Pisces: 'gentle, imaginative, quick to feel what others feel.',
}

const an = (word) => (/^[AEIOU]/i.test(word) ? 'an' : 'a')

export function signWords (sign = '') {
  const words = SIGN_WORDS[sign]
  return words ? `You're ${an(sign)} ${sign}: ${words}` : ''
}

export function signGlyph (sign = '') {
  const i = signIndex(sign)
  return i >= 0 ? GLYPHS[i] : ''
}

export function theirSignLine (sender = '', sign = '') {
  const who = capFirst(String(sender || '').trim())
  if (!who || !SIGN_WORDS[sign]) return "Here's how the two of you meet…"
  return `${who} is ${an(sign)} ${sign}. Here's how the two of you meet…`
}

/** Step 5 when the sender has no birthday yet (gift room walk #8): one line, then on. */
export function noSenderBirthday (sender = '') {
  const who = capFirst(String(sender || '').trim()) || 'They'
  return `${who} hasn't added a birthday yet, so your star together will wait for it.`
}

export const OWN_HERE = "Whenever something of your own comes up, I'm here."

export function ownAction (forSomeone = false) {
  return forSomeone ? { icon: '♡', label: 'Ask for someone' } : { icon: '✦', label: 'Ask my own question' }
}

// ── The bridge's quiet last line (own_bridge.ELSE_LINE on the server) ────

export const ELSE_LINE = '…or ask about anything else on your mind.'

/** The Mystic's first line of their own reading, with the fixed quiet line apart. */
export function splitElseLine (text = '') {
  const t = String(text || '')
  if (!t.trim().endsWith(ELSE_LINE)) return { text: t, quiet: '' }
  return { text: t.trim().slice(0, -ELSE_LINE.length).trim(), quiet: ELSE_LINE }
}

// ── The star ──────────────────────────────────────────────────────────────

/**
 * The star of the two of you as a radar: five arms, each one of the five
 * scores from a 25% floor to full, so every pair's star is its own shape
 * (DESIGN.md "Step 6"). `size` is the star's height; the view is wider for
 * the labels.
 */
export function starShape (arms = [], { size = 260 } = {}) {
  const S = Number(size) || 260
  // 1.3x the height (it was 1.7x): with the words centred on their points a
  // phone keeps the star near full size, and the words readable (e2e round 2).
  const vw = Math.round(S * 1.3)
  const vh = S + 16
  const cx = vw / 2
  const cy = S / 2 + 8
  const r = S * 0.4
  const pt = (a, rad) => [cx + rad * Math.cos(a * Math.PI / 180), cy + rad * Math.sin(a * Math.PI / 180)]
  const f = (n) => n.toFixed(1)
  const list = (Array.isArray(arms) ? arms : []).slice(0, 5)
  if (list.length !== 5) return { vw, vh, cx, cy, r, arms: [], guides: [] }
  const out = list.map((arm, i) => {
    const v = Math.max(0, Math.min(100, Number(arm.value) || 0))
    const angle = -90 + 72 * i
    const tip = pt(angle, r * (0.25 + 0.75 * v / 100))
    const left = pt(angle - 36, r * 0.24)
    const right = pt(angle + 36, r * 0.24)
    // Each word centred on its point: the top one over the ring, the side ones
    // just over the arm's end, the lower two under the ring.
    const sin = Math.sin(angle * Math.PI / 180)
    let labelAt
    if (sin < -0.9) {
      const lab = pt(angle, r + 14)
      labelAt = { x: lab[0], y: lab[1] + 4, anchor: 'middle' }
    } else if (sin > 0.5) {
      const lab = pt(angle, r + 14)
      labelAt = { x: lab[0], y: cy + r + 15, anchor: 'middle' }
    } else {
      const end = pt(angle, r)
      labelAt = { x: end[0], y: end[1] - 11, anchor: 'middle' }
    }
    return {
      ...arm, value: v, angle, tip,
      path: `M${f(cx)} ${f(cy)} L${f(left[0])} ${f(left[1])} L${f(tip[0])} ${f(tip[1])} L${f(right[0])} ${f(right[1])}Z`,
      labelAt,
    }
  })
  return { vw, vh, cx, cy, r, arms: out, guides: [0.25, 0.5, 0.75, 1].map((k) => r * k) }
}
